/**
 * Responsive gameplay layout (portrait-first, 360px phones → 27" desktops). Works in CSS px
 * and returns device-px rectangles; tiles never drop below ~44 CSS px when the screen allows.
 */
import type { CompiledLevel } from '@mp/core';
import { boardPieces } from '../views/dispensers.js';
import { vp } from './viewport.js';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PlayLayout {
  wide: boolean;
  /** tile edge in device px (one tile = 2 half-units) */
  tile: number;
  /** board origin (device px) of half-unit (minX, minY) */
  boardX: number;
  boardY: number;
  minX: number;
  minY: number;
  board: Rect;
  order: Rect;
  conveyor: Rect | null;
  /** shelf (rules v2): the strip its row stands in, where the conveyor would be (a level never has both) */
  shelf: Rect | null;
  bag: Rect;
  boosters: Rect | null;
  /** vertical lift per layer so stacks read as 3D */
  lift: number;
}

/** The safe-area insets in CSS px: the same four `env()` values ui/styles.css reads as `--safe-t/b/l/r`. */
export function safeInsets(): { top: number; bottom: number; left: number; right: number } {
  const probe = document.getElementById('safe-probe') ?? createProbe();
  const cs = getComputedStyle(probe);
  return {
    top: parseFloat(cs.paddingTop) || 0,
    bottom: parseFloat(cs.paddingBottom) || 0,
    left: parseFloat(cs.paddingLeft) || 0,
    right: parseFloat(cs.paddingRight) || 0,
  };
}

function createProbe(): HTMLElement {
  const el = document.createElement('div');
  el.id = 'safe-probe';
  el.style.cssText =
    'position:fixed;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px);padding-left:env(safe-area-inset-left,0px);padding-right:env(safe-area-inset-right,0px)';
  document.body.appendChild(el);
  return el;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export function playLayout(L: CompiledLevel, opts: { boosters: boolean }): PlayLayout {
  const u = vp.u;
  const W = vp.cssW;
  const H = vp.cssH;
  const safe = safeInsets();
  // board extent in half-units (only rows/cols the level uses; dispenser machines are board cells too)
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let maxLayer = 0;
  for (const i of boardPieces(L)) {
    minX = Math.min(minX, L.x[i]!);
    maxX = Math.max(maxX, L.x[i]!);
    minY = Math.min(minY, L.y[i]!);
    maxY = Math.max(maxY, L.y[i]!);
    maxLayer = Math.max(maxLayer, L.layer[i]!);
  }
  if (!Number.isFinite(minX)) {
    minX = maxX = minY = maxY = 0;
  }
  const tilesW = (maxX - minX + 2) / 2;
  const tilesH = (maxY - minY + 2) / 2;
  const hasConv = L.nConv > 0;
  const hasShelf = L.nShelves > 0;

  const wide = W / H > 1.15 && W >= 700;
  const top = safe.top + 64;
  const bottom = H - safe.bottom - 10;
  let order: Rect;
  let conveyor: Rect | null = null;
  let shelf: Rect | null = null;
  let bag: Rect;
  let boosters: Rect | null = null;
  let board: Rect;
  let T: number;

  if (wide) {
    // order ticket on the left, board centre, bag + boosters below the board
    const colW = Math.min(W - 40, H * 1.25);
    const x0 = (W - colW) / 2;
    const sideW = clamp(colW * 0.3, 220, 340);
    const bagH = clamp(H * 0.13, 80, 120);
    const boostH = opts.boosters ? clamp(H * 0.09, 60, 80) : 0;
    const convH = hasConv ? clamp(H * 0.1, 64, 96) : 0;
    const shelfH = hasShelf ? clamp(H * 0.1, 64, 96) : 0;
    const boardW = colW - sideW - 24;
    const boardH = bottom - top - bagH - boostH - convH - shelfH - 16;
    T = Math.min(boardW / (tilesW + 0.4), boardH / (tilesH + 0.3 + maxLayer * 0.06), 120);
    order = { x: x0, y: top + 8, w: sideW, h: Math.min(boardH - 8, 440) };
    const bx = x0 + sideW + 24;
    board = { x: bx, y: top, w: boardW, h: boardH };
    let y = top + boardH + 8;
    if (hasConv) {
      conveyor = { x: bx, y, w: boardW, h: convH };
      y += convH;
    }
    if (hasShelf) {
      shelf = { x: bx, y, w: boardW, h: shelfH };
      y += shelfH;
    }
    bag = { x: x0 + sideW * 0.5, y, w: colW - sideW * 0.5, h: bagH };
    y += bagH;
    if (opts.boosters) boosters = { x: bx, y, w: boardW, h: boostH };
  } else {
    const colW = Math.min(W, 620);
    const x0 = (W - colW) / 2;
    let bagH = clamp(H * 0.11, 66, 110);
    const boostH = opts.boosters ? clamp(H * 0.075, 56, 76) : 0;
    const convH = hasConv ? clamp(H * 0.085, 56, 86) : 0;
    const shelfH = hasShelf ? clamp(H * 0.085, 56, 86) : 0;
    let orderH = clamp(H * 0.15, 92, 150);
    const avail = bottom - top - orderH - bagH - boostH - convH - shelfH - 12;
    const bw = colW - 20;
    T = Math.min(bw / (tilesW + 0.25), avail / (tilesH + 0.25 + maxLayer * 0.06), 104);
    // spread leftover height: a taller ticket and bag, small gaps, the rest becomes counter
    let slack = Math.max(0, avail - (tilesH + 0.25 + maxLayer * 0.06) * T);
    const growOrder = Math.min(slack * 0.25, 40);
    orderH += growOrder;
    const growBag = Math.min((slack - growOrder) * 0.25, 26);
    bagH += growBag;
    slack -= growOrder + growBag;
    const gapTop = Math.min(slack * 0.35, 48);
    const gapMid = Math.min(slack * 0.3, 40);
    const boardH = (tilesH + 0.25 + maxLayer * 0.06) * T;
    order = { x: x0 + 8, y: top, w: colW - 16, h: orderH };
    board = { x: x0 + 10, y: top + orderH + 4 + gapTop, w: bw, h: boardH };
    let y = board.y + boardH + 6 + gapMid;
    if (hasConv) {
      conveyor = { x: x0 + 10, y, w: bw, h: convH };
      y += convH;
    }
    if (hasShelf) {
      shelf = { x: x0 + 10, y, w: bw, h: shelfH };
      y += shelfH;
    }
    bag = { x: x0 + 6, y, w: colW - 12, h: bagH };
    y += bagH;
    if (opts.boosters) boosters = { x: x0 + 10, y, w: bw, h: boostH };
  }

  const lift = T * 0.06;
  const pixW = tilesW * T;
  const pixH = tilesH * T + maxLayer * lift;
  const boardX = board.x + (board.w - pixW) / 2;
  const boardY = board.y + (board.h - pixH) / 2 + maxLayer * lift;
  const s = (r: Rect): Rect => ({ x: r.x * u, y: r.y * u, w: r.w * u, h: r.h * u });
  return {
    wide,
    tile: T * u,
    boardX: boardX * u,
    boardY: boardY * u,
    minX,
    minY,
    board: s(board),
    order: s(order),
    conveyor: conveyor ? s(conveyor) : null,
    shelf: shelf ? s(shelf) : null,
    bag: s(bag),
    boosters: boosters ? s(boosters) : null,
    lift: lift * u,
  };
}

/** Centre of board piece i in device px (logical position, used for hit tests and hooks); a dispenser item: its machine's cell. */
export function piecePos(lay: PlayLayout, L: CompiledLevel, i: number): { x: number; y: number } {
  const half = lay.tile / 2;
  return {
    x: lay.boardX + (L.x[i]! - lay.minX) * half + half,
    y: lay.boardY + (L.y[i]! - lay.minY) * half + half - L.layer[i]! * lay.lift,
  };
}
